/**
 * Element dispatcher — v1 build_elements.js split per SPEC §2.1/§4.3 into one
 * module per element type + shared base (elements/base.ts). DOM output must
 * stay BYTE-COMPATIBLE with v1, including every malformation catalogued in
 * research/03 §15 — see the per-builder module headers. Do not "fix" anything
 * here without a BUGS.md entry.
 *
 * Module-eval side effects preserved from the v1 port (REQ-BOOT-1c,
 * REQ-ELEM-8): the six inline-handler window globals below, the
 * window.uploadPhotoEditorImage assignment (photo-editor.ts eval), and the
 * second PreventCheckoutOnEnter ready-binding (research/03 §15.22).
 *
 * jQuery split (research/01 Q1): bare $ = BUNDLED jQuery (webpack
 * ProvidePlugin equivalent); since DIV-004 the theme's copy is never used.
 *
 * BUG-026 / DIV-002 (sanctioned divergence): dialog scaffolding (#dialog,
 * #alert-block, #errDialog) is now emitted unconditionally at the end of
 * buildCalculator() so showDialog() and errDialog paths work even on
 * calculators that have no PriceMarker element.
 */
import $ from "jquery";
import { PreventCheckoutOnEnter, buildDialogBlock, buildErrorBlock } from "../common/common-utils";
import {
  ChangeEvent,
  UpdatePrice,
  PriceUpdation,
} from "../pricing";
import { buildNumberInput } from "./number-input";
import { buildSlider, SliderInput } from "./slider";
import { buildDropDown } from "./select-list";
import { buildImageSelector } from "./image-selector";
import { buildPhotoPicker } from "./photo-editor";
import { buildTextDisplayBlock } from "./text-display";
import { buildImageElement } from "./image-upload";
import { buildTextInput } from "./long-text";
import { buildCheckboxInput } from "./checkbox";
import { buildRadioElement } from "./radio";
import { buildCalculationElement } from "./calculation";
import { buildLookupInput } from "./lookup";
import { buildPriceMarker } from "./price-marker";
import { CheckNumberInputValidity, CheckTextInputValidity } from "./validity";
import "../styles/slider.css";

// Re-exported so consumers keep importing the elements surface from here
// (bootstrap/{product,nonproduct}-page, checkout/*).
export { CheckNumberInputValidity, CheckTextInputValidity } from "./validity";
export { SliderInput } from "./slider";

window.PriceUpdation = PriceUpdation;
window.CheckNumberInputValidity = CheckNumberInputValidity;
window.CheckTextInputValidity = CheckTextInputValidity;
window.ChangeEvent = ChangeEvent;
window.UpdatePrice = UpdatePrice;
window.SliderInput = SliderInput;

export function buildCalculator(inpData: any): string {
  var div = "";
  inpData.Elements.forEach(function (element: any, index: any) {
    switch (element.ElementType) {
      case "TextInput":
      case "NumberInputNew":
        var txtInp = buildNumberInput(element);
        div += txtInp;
        break;
      case "Slider":
        var sliderInp = buildSlider(element);
        div += sliderInp;
        break;
      case "SelectList":
        var txtDdl = buildDropDown(element);
        div += txtDdl;
        break;
      case "ImageSelector":
        var txtDdl = buildImageSelector(element);
        div += txtDdl;
        break;
      case "PhotoEditor":
        var photoEditor = buildPhotoPicker(element);
        div += photoEditor;
        break;
      case "TextEditor":
        var txtInp = buildTextDisplayBlock(element);
        div += txtInp;
        break;
      case "ImageUpload":
        var imgInp = buildImageElement(element);
        div += imgInp;
        break;
      case "LongTextInput":
        var textInp = buildTextInput(element);
        div += textInp;
        break;
      case "Checkbox":
        var textInp = buildCheckboxInput(element);
        div += textInp;
        break;
      case "Radio":
        var radioInp = buildRadioElement(element);
        div += radioInp;
        break;
      case "Calculation":
        var txtInp = buildCalculationElement(element);
        div += txtInp;
        break;
      case "Lookup":
        var txtInp = buildLookupInput(element);
        div += txtInp;
        break;
      case "PriceMarker":
        var txtInp = buildPriceMarker(element);
        div += txtInp;
        break;
    }
  });

  div += buildVariantElement();
  div += buildCpcPriceElement();

  // BUG-026 / DIV-002: emit dialog scaffolding once per render regardless of
  // calculator composition (PriceMarker already emits it; ensure it's present
  // even on marker-less calculators so showDialog() and #errDialog paths work).
  if (!div.includes("id='dialog'")) {
    const modal = "<div id='errDialog' title='Title' style=display:none;'><p id='dialogMsg'> Message. </p></div>";
    div += buildErrorBlock() + buildDialogBlock() + modal;
  }

  return div;
}

// The two trailing hidden system inputs (REQ-ELEM-1).
//
// variantIdCustom's `name='id'` is load-bearing for the in-form
// SubmitFormData path (submit-form-data.ts's `new FormData(formm)` collects
// it by name for the /cart/add POST) — never remove the attribute. DIV-033:
// its VALUE stays "0" from render until an actual Add to Cart click reaches
// HandleAddToCartEvent, which is also the only place that ever wrote to it.
// Any code on the page that reads "the current variant" generically via
// `[name="id"]` before that point — a third-party app's own click guard,
// analytics, a stock-per-variant widget — reads this element (it renders
// before, and typically precedes in DOM order, any theme-owned `name="id"`
// input the boot-time rename loop missed) and gets the placeholder "0"
// instead of the real variant. find-product-form.ts and product-page.ts now
// keep this VALUE mirrored to the current variant continuously (boot +
// every variant switch), the same real-time treatment DIV-029/032 already
// give cpc_product_default_price/submitButton.disabled — only the value
// changes here, so the FormData behavior above is unaffected.
function buildVariantElement(): string {
  return "<input id='variantIdCustom' name='id' type='hidden' value='0' />";
}

function buildCpcPriceElement(): string {
  return "<input id='cpcPriceToken' name='properties[_cpc_custom_cart_item]' type='hidden' value='none' />";
}

$(window).ready(function () {
  PreventCheckoutOnEnter();
});
